import { IUser } from 'redux/store/user/types'
import { ProfileAbilityBuilder } from '../types'
import { ACTIONS, ROLES, SUBJECTS } from 'features/Permission/constants'
import { isInsideCommunityCondition } from '../conditions'

const grantComplianceLeadershipAbilities = ({ can }: ProfileAbilityBuilder) => {
  can(ACTIONS.VIEW, SUBJECTS.COMPLIANCE)
  can(ACTIONS.DISABLED, SUBJECTS.FORM_HEALTH_SYSTEM_INPUT)
  can(
    ACTIONS.VIEW,
    SUBJECTS.USER_CONTRACT_CATEGORIES_COMMUNITY_LEVEL_SETTINGS,
    isInsideCommunityCondition
  )
  can(
    ACTIONS.VIEW,
    SUBJECTS.USER_CONTRACT_CATEGORIES_ASSIGNMENT_STATUS_SETTINGS
  )
}

const grantCognusRepositoryAbilities = ({ can }: ProfileAbilityBuilder) => {
  can(ACTIONS.VIEW, SUBJECTS.CENTRAL_REPOSITORY)
  can(ACTIONS.VIEW, SUBJECTS.AVAILABLE_FOR_EVALUATION)
  can(ACTIONS.VIEW, SUBJECTS.OPPORTUNITY_ANALYSIS)
  can(ACTIONS.VIEW, SUBJECTS.REQUESTS_FOR_ANALYSIS, isInsideCommunityCondition)
}

const grantPartialCognusRepositoryAbilities = ({
  can
}: ProfileAbilityBuilder) => {
  can(ACTIONS.VIEW, SUBJECTS.CENTRAL_REPOSITORY)
  can(ACTIONS.VIEW, SUBJECTS.AVAILABLE_FOR_EVALUATION)
}

const grantContractCategoriesSettingsAbilities = ({
  can
}: ProfileAbilityBuilder) => {
  can(ACTIONS.VIEW, SUBJECTS.USER_CONTRACT_CATEGORIES_SETTINGS)
}

const grantFormHospitalListEditAbility = (builder: ProfileAbilityBuilder) => {
  builder.can(ACTIONS.EDIT, SUBJECTS.FORM_HOSPITAL_LIST)
}

const defineVicePresidentAbilities = (builder: ProfileAbilityBuilder) => {
  grantComplianceLeadershipAbilities(builder)
  grantContractCategoriesSettingsAbilities(builder)
  grantCognusRepositoryAbilities(builder)
}

const defineContractStewardAbilities = (builder: ProfileAbilityBuilder) => {
  grantComplianceLeadershipAbilities(builder)
  grantContractCategoriesSettingsAbilities(builder)
  grantCognusRepositoryAbilities(builder)
}

const defineHospitalFamilyAbilities = (
  builder: ProfileAbilityBuilder,
  user: IUser
) => {
  const { can } = builder

  if (user.role === ROLES.HOSPITAL_USER) {
    can(ACTIONS.CRUD, SUBJECTS.ADDITIONAL_INFO_PROFILE)
    grantContractCategoriesSettingsAbilities(builder)
  }
}

const defineLegalAbilities = (builder: ProfileAbilityBuilder) => {
  grantContractCategoriesSettingsAbilities(builder)
}

const defineAnalystAbilities = (builder: ProfileAbilityBuilder) => {
  grantContractCategoriesSettingsAbilities(builder)
  grantCognusRepositoryAbilities(builder)
}

const defineClinicalCoordinatorAbilities = (builder: ProfileAbilityBuilder) => {
  const { can } = builder

  can(ACTIONS.EDIT, SUBJECTS.FORM_HOSPITAL_LIST)
  can(
    ACTIONS.VIEW,
    SUBJECTS.USER_CONTRACT_CATEGORIES_COMMUNITY_LEVEL_SETTINGS,
    isInsideCommunityCondition
  )
  grantContractCategoriesSettingsAbilities(builder)
  grantPartialCognusRepositoryAbilities(builder)
}

export const defineCommunityAbilities = (
  builder: ProfileAbilityBuilder,
  user: IUser
) => {
  builder.can(ACTIONS.VIEW, SUBJECTS.MY_COMMUNITY)
  builder.can(
    ACTIONS.VIEW,
    SUBJECTS.COMMUNITIES_TAB,
    isInsideCommunityCondition
  )
  builder.can(ACTIONS.ARCHIVE, SUBJECTS.USER)

  switch (user.role) {
    case ROLES.COMMUNITY_PRESIDENT:
    case ROLES.VICE_PRESIDENT:
    case ROLES.CONTRACT_STEWARD:
      grantFormHospitalListEditAbility(builder)
  }

  switch (user.role) {
    case ROLES.VICE_PRESIDENT:
      defineVicePresidentAbilities(builder)
      break
    case ROLES.CONTRACT_STEWARD:
      defineContractStewardAbilities(builder)
      break
    case ROLES.HOSPITAL_USER:
    case ROLES.HOSPITAL_ADMIN:
      defineHospitalFamilyAbilities(builder, user)
      break
    case ROLES.LEGAL:
      defineLegalAbilities(builder)
      break
    case ROLES.ANALYST:
      defineAnalystAbilities(builder)
      break
    case ROLES.CLINICAL_COORDINATOR:
      defineClinicalCoordinatorAbilities(builder)
      break
    case ROLES.COMMUNITY_RCA:
      break
  }
}
